import { create } from 'zustand';

import { api } from '../lib/api.js';
import type { TreeEntry } from '../lib/tree.js';

/**
 * The project's file listing, shared by the Assets browser, the scene and
 * model pickers, and the command palette. Refreshed when files change on disk.
 */

export type AssetKind =
  | 'folder'
  | 'scene'
  | 'model'
  | 'pixel'
  | 'sound'
  | 'video'
  | 'prefab'
  | 'material'
  | 'script'
  | 'data'
  | 'ui'
  | 'mesh'
  | 'texture'
  | 'audio'
  | 'font'
  | 'project'
  | 'doc'
  | 'file';

export function assetKind(entry: Pick<TreeEntry, 'name' | 'kind'>): AssetKind {
  if (entry.kind === 'directory') return 'folder';
  const name = entry.name.toLowerCase();
  if (name.endsWith('.scene.json')) return 'scene';
  if (name.endsWith('.model.json')) return 'model';
  if (name.endsWith('.pixel.json')) return 'pixel';
  if (name.endsWith('.sound.json')) return 'sound';
  if (name.endsWith('.video.json')) return 'video';
  if (name.endsWith('.prefab.json')) return 'prefab';
  if (name.endsWith('.material.json')) return 'material';
  if (name.endsWith('.ui.json')) return 'ui';
  // forge.project.json: projects made before the Forge -> Prism rename.
  if (name === 'prism.project.json' || name === 'forge.project.json') return 'project';
  if (name.endsWith('.rhai')) return 'script';
  if (name.endsWith('.json')) return 'data';
  if (/\.(glb|gltf|obj|fbx)$/.test(name)) return 'mesh';
  if (/\.(png|jpe?g|webp|gif|bmp|tga|ktx2?)$/.test(name)) return 'texture';
  if (/\.(wav|ogg|mp3|flac)$/.test(name)) return 'audio';
  if (/\.(ttf|otf)$/.test(name)) return 'font';
  if (/\.(md|txt)$/.test(name)) return 'doc';
  return 'file';
}

const HIDDEN = /(^|\/)(\.git|node_modules|\.prism|target)(\/|$)/;

interface FilesState {
  projectId: number | null;
  entries: TreeEntry[];
  /** From prism.project.json, when the project is a Prism project. */
  isPrismProject: boolean;
  startScene: string | null;
  /** `settings.layers` from prism.project.json (default ["Default"]). */
  layers: string[];
  loading: boolean;
  error: string | null;
  load: (projectId: number) => Promise<void>;
  /** Coalesced reload after file-change events. */
  refreshSoon: () => void;
  reset: () => void;
}

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

export const useProjectFiles = create<FilesState>((set, get) => ({
  projectId: null,
  entries: [],
  isPrismProject: false,
  startScene: null,
  layers: ['Default'],
  loading: false,
  error: null,

  load: async (projectId) => {
    set({ projectId, loading: true });
    try {
      const result = await api.get<{ entries: TreeEntry[] }>(`/projects/${projectId}/files?depth=5`);
      if (get().projectId !== projectId) return;
      const entries = result.entries
        .map((e) => ({ ...e, path: e.path.replace(/\\/g, '/') }))
        .filter((e) => !HIDDEN.test(e.path));
      const isPrismProject = entries.some((e) => e.path === 'prism.project.json');
      let startScene: string | null = null;
      let layers = ['Default'];
      if (isPrismProject) {
        try {
          const manifest = await api.get<{ file: { content: string } }>(
            `/projects/${projectId}/file?path=prism.project.json`,
          );
          const parsed = JSON.parse(manifest.file.content) as { startScene?: unknown; settings?: { layers?: unknown } };
          if (typeof parsed.startScene === 'string') startScene = parsed.startScene;
          const l = parsed.settings?.layers;
          if (Array.isArray(l) && l.length > 0 && l.every((x) => typeof x === 'string')) layers = l as string[];
        } catch {
          // A broken manifest is reported by `prism check`; the editor still lists files.
        }
      }
      if (get().projectId !== projectId) return;
      set({ entries, isPrismProject, startScene, layers, loading: false, error: null });
    } catch (error) {
      set({ loading: false, error: error instanceof Error ? error.message : 'Could not list files.' });
    }
  },

  refreshSoon: () => {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      const { projectId } = get();
      if (projectId) void get().load(projectId);
    }, 600);
  },

  reset: () =>
    set({ projectId: null, entries: [], isPrismProject: false, startScene: null, layers: ['Default'], loading: false, error: null }),
}));

export function filesOfKind(entries: readonly TreeEntry[], kind: AssetKind): string[] {
  return entries
    .filter((e) => e.kind === 'file' && assetKind(e) === kind)
    .map((e) => e.path)
    .sort((a, b) => a.localeCompare(b));
}
